import { useEffect, type JSX } from 'react';
import { useStore } from 'zustand';
import { useProject, useUi } from '../../context';
import { selectTheme } from '../../uiStore';
import { useTicker } from '../../hooks/useTicker';
import {
  adOfferStore,
  canDecide,
  clockText,
  DECIDE_WINDOW_MS,
  detailText,
  formatMoney,
  isClosed,
  offerLabels,
  remainingMs,
  secondsLeft,
  statusText,
  whatText,
  type StudioOfferCard,
} from './model';
import './adOffers.css';

/** Where the creator answers when the studio cannot: the portal's Stream Manager. */
function portalLink(portalUrl: string | undefined): string | null {
  if (!portalUrl) return null;
  try {
    const origin = new URL(portalUrl).origin;
    return origin === 'null' ? null : `${origin}/dashboard/stream-manager`;
  } catch {
    return null;
  }
}

function OfferCard({ card, now, readOnly, link, theme }: { card: StudioOfferCard; now: number; readOnly: boolean; link: string | null; theme: 'light' | 'dark' }): JSX.Element {
  const { offer } = card;
  const left = remainingMs(card, now);
  const open = canDecide(card, now);
  const closed = isClosed(card);
  const line = statusText(card, now);
  const urgent = left <= 10_000;
  const labels = offerLabels(offer);
  const decide = (decision: 'accept' | 'reject'): void => void adOfferStore.getState().decide(offer.offer_id, decision, Date.now());

  return (
    <section className="ad-offer-card" data-card-theme={theme} data-spin={closed ? undefined : ''} aria-label={`Oferta de anuncio de ${offer.brand.name}`}>
      <div className="ad-offer-head">
        {card.logoUrl ? (
          <img className="ad-offer-logo" src={card.logoUrl} alt="" />
        ) : (
          <span className="ad-offer-logo" aria-hidden="true">
            {offer.brand.name.slice(0, 1).toUpperCase()}
          </span>
        )}
        <div className="ui-grow ad-offer-brand">
          <strong className="ui-ellipsis">{offer.brand.name}</strong>
          {labels.length > 0 ? <span className="ui-dim">{labels.join(' · ')}</span> : null}
        </div>
        {closed ? null : (
          <span role="timer" className={`ad-offer-timer${urgent ? ' urgent' : ''}`} aria-label={`${secondsLeft(left)} segundos para responder`}>
            {clockText(left)}
          </span>
        )}
      </div>
      <div>
        <div className="ad-offer-net">Ganás {formatMoney(offer.net_cents, offer.currency)}</div>
        <div className="ui-dim ad-offer-small">{detailText(offer)}</div>
      </div>
      <div className="ad-offer-what">
        {card.previewUrl ? <img className="ad-offer-preview" src={card.previewUrl} alt={`Vista previa del anuncio de ${offer.brand.name}`} /> : null}
        <div>
          <div>{whatText(offer)}</div>
          <div className="ui-dim">{offer.when_text}</div>
        </div>
      </div>
      {closed ? null : (
        <div className="ad-offer-bar" aria-hidden="true">
          <div className={urgent ? 'urgent' : ''} style={{ width: `${Math.min(100, (100 * left) / DECIDE_WINDOW_MS)}%` }} />
        </div>
      )}
      {closed ? null : readOnly ? (
        <p className="ad-offer-readonly">
          Esta ventana del studio no puede responder ofertas.{' '}
          {link ? (
            <a href={link} target="_blank" rel="noopener noreferrer">
              Responder en Perck
            </a>
          ) : null}
        </p>
      ) : (
        <div className="ad-offer-actions">
          <button type="button" className="ui-btn" disabled={!open} onClick={() => decide('reject')}>
            Rechazar
          </button>
          <button type="button" className="ui-btn primary" disabled={!open} onClick={() => decide('accept')}>
            Aceptar
          </button>
        </div>
      )}
      {line ? (
        <p role="status" className="ad-offer-status">
          {line}
        </p>
      ) : null}
    </section>
  );
}

/**
 * The creator's ad offers, as the portal shows them. Embedded in the portal's dashboard the creator answers here (the
 * portal makes the decision with its login); standalone the dock is read-only and points at the portal.
 */
export function AdOffersPanel(): JSX.Element {
  const cards = useStore(adOfferStore, (s) => s.cards);
  const readOnly = useStore(adOfferStore, (s) => s.decider === null);
  const native = useStore(adOfferStore, (s) => s.native);
  // The card stands out against Studio: drawn in the opposite theme.
  const cardTheme = useUi(selectTheme) === 'dark' ? 'light' : 'dark';
  const portalUrl = useProject((p) => p.channel.portalUrl);
  const link = portalLink(portalUrl);
  const now = useTicker(cards.length > 0, 250);

  // Unanswered cards close as Venció when their window passes; closed ones go after their notice.
  useEffect(() => {
    if (cards.length > 0) adOfferStore.getState().tick(now);
  }, [cards.length, now]);

  if (cards.length === 0 || native) {
    return (
      <div className="ui-panel-body">
        <div className="ui-empty">
          {native ? (
            'Se acepta en OMP Tools: las ofertas de anuncios se responden en su tarjeta, en tu escritorio.'
          ) : readOnly ? (
            <>
              Las ofertas de anuncios se responden en Perck.{' '}
              {link ? (
                <a href={link} target="_blank" rel="noopener noreferrer">
                  Abrir el Stream Manager
                </a>
              ) : null}
            </>
          ) : (
            'No hay ofertas de anuncios pendientes.'
          )}
        </div>
      </div>
    );
  }
  return (
    <div className="ui-panel-body ad-offers" aria-label="Ofertas de anuncios" aria-live="polite">
      {cards.map((card) => (
        <OfferCard key={card.offer.offer_id} card={card} now={now} readOnly={readOnly} link={link} theme={cardTheme} />
      ))}
    </div>
  );
}
